import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface CompetitorData {
  url: string;
  color: string;
  metrics: {
    seo: number;
    geo: number;
    speed: number;
    mobile: number;
    content: number;
    schema: number;
  };
}

const yourSite: CompetitorData = {
  url: "magazin.ro",
  color: "hsl(158, 64%, 42%)",
  metrics: {
    seo: 72,
    geo: 45,
    speed: 68,
    mobile: 82,
    content: 55,
    schema: 40,
  },
};

const initialCompetitors: CompetitorData[] = [
  {
    url: "competitor-a.ro",
    color: "hsl(220, 70%, 50%)",
    metrics: {
      seo: 68,
      geo: 52,
      speed: 75,
      mobile: 70,
      content: 60,
      schema: 55,
    },
  },
];

export function CompetitorAnalysis() {
  const [competitors, setCompetitors] = useState<CompetitorData[]>(initialCompetitors);
  const [newUrl, setNewUrl] = useState("");

  const addCompetitor = () => {
    if (!newUrl || competitors.length >= 3) return;
    
    const colors = [
      "hsl(340, 70%, 50%)",
      "hsl(45, 90%, 50%)",
      "hsl(280, 70%, 50%)",
    ];
    
    setCompetitors([
      ...competitors,
      {
        url: newUrl,
        color: colors[competitors.length],
        metrics: {
          seo: Math.floor(Math.random() * 40) + 50,
          geo: Math.floor(Math.random() * 40) + 30,
          speed: Math.floor(Math.random() * 40) + 50,
          mobile: Math.floor(Math.random() * 30) + 60,
          content: Math.floor(Math.random() * 40) + 40,
          schema: Math.floor(Math.random() * 50) + 30,
        },
      },
    ]);
    setNewUrl("");
  };

  const removeCompetitor = (url: string) => {
    setCompetitors(competitors.filter((c) => c.url !== url));
  };

  const chartData = [
    { metric: "SEO", [yourSite.url]: yourSite.metrics.seo, ...Object.fromEntries(competitors.map(c => [c.url, c.metrics.seo])) },
    { metric: "GEO", [yourSite.url]: yourSite.metrics.geo, ...Object.fromEntries(competitors.map(c => [c.url, c.metrics.geo])) },
    { metric: "Viteză", [yourSite.url]: yourSite.metrics.speed, ...Object.fromEntries(competitors.map(c => [c.url, c.metrics.speed])) },
    { metric: "Mobile", [yourSite.url]: yourSite.metrics.mobile, ...Object.fromEntries(competitors.map(c => [c.url, c.metrics.mobile])) },
    { metric: "Conținut", [yourSite.url]: yourSite.metrics.content, ...Object.fromEntries(competitors.map(c => [c.url, c.metrics.content])) },
    { metric: "Schema", [yourSite.url]: yourSite.metrics.schema, ...Object.fromEntries(competitors.map(c => [c.url, c.metrics.schema])) },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-elevated p-6"
    >
      <h3 className="text-lg font-semibold text-foreground mb-2">
        Analiză Concurență
      </h3>
      <p className="text-sm text-muted-foreground mb-6">
        Compară vizibilitatea SEO/GEO cu competitorii tăi
      </p>

      {/* Add Competitor */}
      <div className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Introdu URL competitor..."
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          onClick={addCompetitor}
          disabled={!newUrl || competitors.length >= 3}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Adaugă
        </Button>
      </div>

      {/* Competitor Tags */}
      <div className="flex flex-wrap gap-2 mb-6">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
          style={{ backgroundColor: `${yourSite.color}20`, color: yourSite.color }}
        >
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: yourSite.color }}
          />
          {yourSite.url} (Tu)
        </div>
        {competitors.map((competitor) => (
          <div
            key={competitor.url}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
            style={{
              backgroundColor: `${competitor.color}20`,
              color: competitor.color,
            }}
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: competitor.color }}
            />
            {competitor.url}
            <button
              onClick={() => removeCompetitor(competitor.url)}
              className="hover:opacity-70 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Radar Chart */}
      <div className="h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData}>
            <PolarGrid stroke="hsl(var(--border))" />
            <PolarAngleAxis
              dataKey="metric"
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
            />
            <PolarRadiusAxis
              angle={30}
              domain={[0, 100]}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
            />
            <Radar
              name={yourSite.url}
              dataKey={yourSite.url}
              stroke={yourSite.color}
              fill={yourSite.color}
              fillOpacity={0.3}
              strokeWidth={2}
            />
            {competitors.map((competitor) => (
              <Radar
                key={competitor.url}
                name={competitor.url}
                dataKey={competitor.url}
                stroke={competitor.color}
                fill={competitor.color}
                fillOpacity={0.15}
                strokeWidth={2}
              />
            ))}
            <Legend />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}
